import { Fab, Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ChatBubbleOutlineIcon } from '@/components/icons';
import type { ChatButtonAccent } from '@/lib/chat-customization';
import { scaledFontSize } from './createWidgetTheme';
import {
  getLauncherFabSx,
  getLauncherIconMetrics,
  LAUNCHER_FAB_SIZE,
  resolveLauncherAccentStyles,
} from '@/lib/launcher-accent-styles';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

interface ChatFloatingButtonFabProps {
  showLabel: boolean;
  agentName: string;
  chatButtonAccent: ChatButtonAccent;
  fabPulseSx: SxProps<Theme>;
  launcherRef: React.RefObject<HTMLButtonElement | null>;
  onClick: () => void;
}

function LauncherIcon({
  accentStyles,
  fabSize = LAUNCHER_FAB_SIZE,
}: {
  accentStyles: ReturnType<typeof resolveLauncherAccentStyles>;
  fabSize?: number;
}) {
  const hasPuck = Boolean(accentStyles.iconPuck);
  const { puckSize, iconSize } = getLauncherIconMetrics(fabSize, hasPuck);

  if (accentStyles.iconPuck) {
    return (
      <Box
        sx={{
          width: puckSize,
          height: puckSize,
          borderRadius: '50%',
          bgcolor: accentStyles.iconPuck.backgroundColor,
          color: accentStyles.iconPuck.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <ChatBubbleOutlineIcon aria-hidden sx={{ fontSize: iconSize }} />
      </Box>
    );
  }

  return <ChatBubbleOutlineIcon aria-hidden sx={{ fontSize: iconSize, color: accentStyles.iconColor }} />;
}

export function ChatFloatingButtonFab({
  showLabel,
  agentName,
  chatButtonAccent,
  fabPulseSx,
  launcherRef,
  onClick,
}: ChatFloatingButtonFabProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const openChatLabel = t('launcher.openChat');
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const accentStyles = resolveLauncherAccentStyles({
    accent: chatButtonAccent,
    primary,
    secondary,
  });
  const fabBaseSx = getLauncherFabSx(chatButtonAccent, primary, secondary);

  if (showLabel) {
    return (
      <Fab
        ref={launcherRef}
        variant="extended"
        onClick={onClick}
        aria-label={openChatLabel}
        title={openChatLabel}
        lang={locale}
        sx={
          [
            fabBaseSx,
            fabPulseSx,
            {
              pl: accentStyles.iconPuck ? 0.5 : 2.5,
              pr: 2.5,
              height: LAUNCHER_FAB_SIZE,
              fontWeight: 500,
              fontSize: scaledFontSize(theme, 15),
              lineHeight: 1.25,
              borderRadius: '9999px',
              display: 'flex',
              alignItems: 'center',
              gap: accentStyles.iconPuck ? 1 : 1.5,
              textTransform: 'none',
              pointerEvents: 'auto',
              color: accentStyles.labelColor,
            },
          ] as SxProps<Theme>
        }
      >
        <LauncherIcon accentStyles={accentStyles} />
        <span aria-hidden style={{ color: accentStyles.labelColor }}>
          {agentName}
        </span>
      </Fab>
    );
  }

  return (
    <Fab
      ref={launcherRef}
      onClick={onClick}
      aria-label={openChatLabel}
      title={openChatLabel}
      lang={locale}
      sx={
        [
          fabBaseSx,
          fabPulseSx,
          {
            width: LAUNCHER_FAB_SIZE,
            height: LAUNCHER_FAB_SIZE,
            minHeight: LAUNCHER_FAB_SIZE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          },
        ] as SxProps<Theme>
      }
    >
      <LauncherIcon accentStyles={accentStyles} />
    </Fab>
  );
}
